import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { RosterAttendeeType, useContentShareControls, useLocalVideo, useMeetingManager, useRosterState } from "amazon-chime-sdk-component-library-react";
import {
  CreateAttendeeCommandOutput,
  CreateMeetingCommandOutput,
  GetMeetingCommandOutput,
} from "@aws-sdk/client-chime-sdk-meetings";
import { ConsoleLogger, LogLevel, MeetingSessionConfiguration } from "amazon-chime-sdk-js";
import { createChimeMeeting } from "./helpers/create-meeting";
import { getChimeMeeting } from "./helpers/get-meeting";
import { createChimeAttendee } from "./helpers/create-attendee";
import { chimeStartRecording } from "./helpers/start-recording";
import { chaimeStopRecording } from "./helpers/stop-recording";
import { deleteChimeMeeting } from "./helpers/delete-meeting";
import ToasterWithMessage from "../components/toaster";
import { GET_USER_BY_ID } from "../pages/LiveClass/api/LiveClassQuery";
import { useLazyQuery, useMutation } from "@apollo/client";
import { deleteChimeAttendee } from "./helpers/delete-attendee";
import { getColorFromUUID } from "../utils/chimeHelper";
import { store } from "../redux/app/configureStore";
import { trackMettingEvent } from "../firebase/analyticsHelper";
import { captureEvent, updateUserProperties } from "../utils/PostHogHelper";
import { useAppSelector } from "../redux/app/hooks";
import { FETCH_POLICY, FETCH_POLICY_NO_CACHE, HAND_RAISE, LIVE_UPDATE, SCREEN_RECORDING, SCREEN_SHARING } from "../utils/constants";
import { UPDATE_WORKSHOP_MEETING_ID } from "../pages/Workshop/api/WorkshopQuery";
import { useDispatch } from "react-redux";
import { setCurrentSessionDetail } from "../redux/slices/dashboardSlice";
import { UPDATE_LIVECLASS_MEDIA_PIPELINE_INFO, UPDATE_PTA_MEDIA_PIPELINE_INFO } from "./helpers/Api/ChimeApi";
import { UPDATE_MEETING_STATUS } from "../pages/AdminPta/api/PtaQuery";

const { error: errorToast } = ToasterWithMessage();

interface MeetingContextType {
  createMeeting: (hostId: string) => Promise<CreateMeetingCommandOutput | undefined>;
  joinMeeting: (
    meetingId: string,
    attendeeId: string,
    HostId: string,
    originalHostId : string,
    hostAttendee: string
  ) => Promise<{
    meetingResponse: GetMeetingCommandOutput | undefined;
    attendeeResponse: CreateAttendeeCommandOutput | undefined;
  }>;
  toggleAudio: () => Promise<void>;
  toggleVideo: () => Promise<void>;
  leaveMeeting: () => Promise<void>;
  startRecording: (recordingStartTime:string, workshopId?: string) => Promise<void>;
  stopRecording: () => Promise<void>;
  endMeeting: () => Promise<void>;
  attendeeId: string | null;
  isHost?: boolean;
  isAudioEnabled: boolean;
  isVideoEnabled: boolean;
  isRecording: boolean;
  participantsRef: any;
  attendeesDataRef: any;
  attendeesData: string[];
  participantList: string[];
  setAttendeesData: Dispatch<SetStateAction<string[]>>;
  setParticipantsList: Dispatch<SetStateAction<string[]>>;
  toggleHandRaise: any;
  subscribeToHandRaise: any;
  isHandRaised: boolean;
  setIsHandRaised: Dispatch<SetStateAction<boolean>>;
  hostId: string | null;
  setHostId : Dispatch<SetStateAction<string | null>>;
  meetingId: string | null;
  muteAll: () => Promise<void>;
  setRemoteSharing: Dispatch<SetStateAction<boolean>>;
  isRemoteSharing: boolean;
  setRemoteSharingUser: Dispatch<SetStateAction<RosterAttendeeType & { color: string } | null>>;
  remoteSharingUser: RosterAttendeeType & { color: string } | null;
  recordingStartTime: string | null;
  muteParticipant: (id: string) => Promise<void>;
  toggleScreenShare: () => Promise<void>;
  startScreenShare: (attendee: RosterAttendeeType & { color: string }) => void;
  stopScreenShare: () => void;
  setReadCount: Dispatch<SetStateAction<number>>;
  readCount:number;
  setRecordingStartTime: Dispatch<SetStateAction<string | null>>;
  userId: string | null;
  setIsRecording: Dispatch<SetStateAction<boolean>>;
  startRecordingInProgress: boolean;
  stopRecordingInProgress: boolean;
  setIsShowParticipants: (val: boolean) => void;
  isShowParticipants: boolean;
  hasNewChatMessages: boolean;
  setHasNewChatMessages: Dispatch<SetStateAction<boolean>>;
  showChat: boolean;
  setShowChat: Dispatch<SetStateAction<boolean>>;
  mediaPipelineId : string | null;
  mediaPipelineArn : string | null;
  setMediaPipelineArn :  Dispatch<SetStateAction<string | null>>
  setMediaPipelineId :  Dispatch<SetStateAction<string | null>>
  setHostAttendeeId: Dispatch<SetStateAction<string | null>>;
  hostAttendeeId: string | null;
}

interface MeetingProviderProps {
  children: ReactNode;
}

const ChimeMeetingContext = createContext<MeetingContextType | undefined>(undefined);

export const ChimeMeetingProvider: React.FC<MeetingProviderProps> = ({ children }) => {
  const meetingManager = useMeetingManager();
  const [attendeeId, setAttendeeId] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [meetingId, setMeetingId] = useState<string | null>(null);
  const [hostId, setHostId] = useState<string | null>(null);
  const [hostAttendeeId, setHostAttendeeId] = useState<string | null>(null);
  const [isHost, setIsHost] = useState<boolean>(false);
  const [isAudioEnabled, setIsAudioEnabled] = useState<boolean>(true);
  const { isVideoEnabled, setIsVideoEnabled } = useLocalVideo();
  const [isRecording, setIsRecording] = useState<boolean>(false);
  const [recordingStartTime, setRecordingStartTime] = useState<string | null>(null);
  const [mediaPipelineId, setMediaPipelineId] = useState<string | null>(null);
  const [mediaPipelineArn, setMediaPipelineArn] = useState<string | null>(null);
  const [isHandRaised, setIsHandRaised] = useState<boolean>(false);
  const attendeesDataRef = useRef<any[]>([]);
  const participantsRef = useRef<any>({});
  const [attendeesData, setAttendeesData] = useState<string[]>([]);
  const [participantList, setParticipantsList] = useState<string[]>([]);
  const [isRemoteSharing, setRemoteSharing] = useState<boolean>(false);
  const [remoteSharingUser, setRemoteSharingUser] = useState<RosterAttendeeType & { color: string }|null>({chimeAttendeeId:'',externalUserId:'',name:'', color:''});
  const { toggleContentShare } = useContentShareControls();
  const [readCount,setReadCount] = useState<number>(0);
  const [startRecordingInProgress, setStartRecordingInProgress] = useState(false);
  const [stopRecordingInProgress, setStopRecordingInProgress] = useState(false);
  const { role, loginpage } = useAppSelector((state) => state.auth);
  const { meetComponent } = useAppSelector((state) => state?.dashboard);
  const selectedClassRoomId = useAppSelector((state) => state.auth.selectedClassRoomId);
  const selectedSubjectId = localStorage.getItem('selectedSId');
  
  const [isShowParticipants, setIsShowParticipants]=useState<boolean>(false)
  const [hasNewChatMessages, setHasNewChatMessages] = useState(false);
  const { roster } = useRosterState();
  const [showChat, setShowChat] = useState(false);
  const dispatch = useDispatch()

  useEffect(() => {
    console.log("[Log] roster changed", roster);
  }, [roster]);

  useEffect(() => {
    console.log("[Log] Recording start time changed", recordingStartTime);
  }, [recordingStartTime]);

  // Configure the devices
  const configureDevices = useCallback(async () => {
    const videoInputDevices =
      await meetingManager.meetingSession?.audioVideo?.listVideoInputDevices();
    const audioInputDevices =
      await meetingManager.meetingSession?.audioVideo?.listAudioInputDevices();
    const audioOutputDevices =
      await meetingManager.meetingSession?.audioVideo?.listAudioOutputDevices();
    const { defaultAudioMute } = store?.getState()?.auth;

    if (videoInputDevices?.length) {

      const selfAttendeeId = meetingManager.meetingSession?.configuration.credentials?.attendeeId;
      console.log("self attendedd id ===========", selfAttendeeId);

    console.log(" ",attendeeId)
    await meetingManager.selectVideoInputDevice(videoInputDevices[0].deviceId);
    }

    if (audioInputDevices?.length) {
      await meetingManager.startAudioInputDevice(audioInputDevices[0].deviceId);
      if (defaultAudioMute) {
        console.log("[Log] defaultAudioMute", process.env.defaultAudioMute);
        meetingManager.meetingSession?.audioVideo.realtimeMuteLocalAudio();
      }
    }

    if (audioOutputDevices?.length) {
      await meetingManager.startAudioOutputDevice(audioOutputDevices[0].deviceId);
    }

  }, [meetingManager]);

  const [getUser] = useLazyQuery(GET_USER_BY_ID, {
    fetchPolicy: FETCH_POLICY,
    onError: (err) => {
      console.log("[Log] Getuser error", err?.message);
    }
  });

  // Listen for live updates
  const listenForLiveUpdates = useCallback(() => {
    const audioVideo = meetingManager.meetingSession?.audioVideo;

    if (audioVideo) {
      const handleLiveUpdates = (dataMessage: any) => {
        console.log("[debug] datamessage", dataMessage);
        try {
          const message = JSON.parse(dataMessage.text());
          console.log('message==============>',message)
          if (message?.action === SCREEN_RECORDING) {
            setIsRecording(!!message.time);
            setRecordingStartTime(message.time);
          }

          if (message?.action === SCREEN_SHARING) {
            setRemoteSharing(!!message?.screenSharing);
            setRemoteSharingUser(message?.attendee);
          }

          if (message?.action === "mute-all") {
            audioVideo.realtimeMuteLocalAudio();
            setIsAudioEnabled(false);
          }

        if (message?.action === "host-changed") {
        console.log("the host changed message", message);
        localStorage.setItem("hostId", message.hostId ?? "");
        localStorage.setItem("hostAttendeeId", message.hostAttendeeId ?? "");
        setHostId(message.hostId ?? null);
        setHostAttendeeId(message.hostAttendeeId ?? null);
        setMediaPipelineId(message.mediaPipelineId ?? null);
        setMediaPipelineArn(message.mediaPipelineArn ?? null);
      }


        } catch (error) {
          console.error("Error parsing live update message:", error);
        }
      };

      // Subscribe to recording-status messages
      audioVideo.realtimeSubscribeToReceiveDataMessage(LIVE_UPDATE, handleLiveUpdates);
    }
  }, [meetingManager]);

  // Listen for status request
  // const listenForStatusRequest = useCallback(() => {
  //   const audioVideo = meetingManager.meetingSession?.audioVideo;
  //   console.log("[Log] audioVideo inside status request listener", audioVideo);

  //   if (audioVideo) {
  //     const handleStatusRequest = (dataMessage: any) => {
  //       try {
  //         const message = JSON.parse(dataMessage.text());
  //         console.log("[Log] Received status request:", message);

  //         if (message?.action === "screen-recording-status") {
  //           const response = {
  //             action: "screen-recording",
  //             time: isRecording && recordingStartTime ? recordingStartTime : null,
  //           };
  //           console.log("[Log] responding to status request request", response);
  //           audioVideo.realtimeSendDataMessage("live-update", JSON.stringify(response));
  //         }
  //       } catch (error) {
  //         console.error("Error parsing status request message:", error);
  //       }
  //     };

  //     // Subscribe to recording-status messages
  //     console.log('[Log] Host subscribing to status request messages');
  //     audioVideo.realtimeSubscribeToReceiveDataMessage("status-request", handleStatusRequest);
  //   }
  // }, [meetingManager, isRecording, recordingStartTime]);

    // Mute Functionality
    const listenForMuteParticipant = useCallback(() => {
      const audioVideo = meetingManager?.meetingSession?.audioVideo;
  
      if (audioVideo) {
        const handleMute = (dataMessage: any) => {
          try {
            const message = JSON.parse(dataMessage.text());
            console.log("[Log] Mute message: ", message);
            if (message?.action === "mute" && message?.id === localStorage.getItem("attendeeId")) {
              audioVideo.realtimeMuteLocalAudio();
              setIsAudioEnabled(false);
            }
          } catch (error) {
            console.error("Error parsing mute message:", error);
          }
        };
  
        audioVideo.realtimeSubscribeToReceiveDataMessage("mute", handleMute);
      }
    }, [meetingManager]);

  // Create a meeting (Host)
  const createMeeting = useCallback(
    async (HostId: string) => {
      const meetingResponse = await createChimeMeeting();
      console.log("[test create meeting] meetingResponse", meetingResponse);

      const meetId = meetingResponse!.Meeting!.MeetingId!;

      setMeetingId(meetId);

      const attendeeData = await createChimeAttendee(meetId, HostId);

      setAttendeeId(attendeeData!.Attendee!.AttendeeId!);
      setUserId(attendeeData!.Attendee!.ExternalUserId!);

      // Ensure that media devices are allowed by the user
      meetingManager.meetingSession?.audioVideo.setDeviceLabelTrigger(() =>
        navigator.mediaDevices.getUserMedia({ audio: true, video: true })
      );

      const configuration = new MeetingSessionConfiguration(meetingResponse, attendeeData);

      await meetingManager.join(configuration, {
        deviceLabels: async () =>
          await navigator.mediaDevices.getUserMedia({ audio: true, video: true }),
        enableWebAudio:true
      });

      meetingManager?.meetingSession?.audioVideo?.start();

      meetingManager.getAttendee = async (chimeAttendeeId: string, externalUserId?: string) => {
        if(externalUserId?.includes("aws:MediaPipeline")) {
          console.log("Inside getUser if condition for mediapipeline", externalUserId);
          return {
            name: "Media Pipeline",
            color: "#000000",
          }
        }
        const response = await getUser({ variables: { uId: externalUserId?.split('|')[0] } });
        const color = getColorFromUUID(externalUserId?.split('|')[0]!);
        console.log("[Log] getUser response", color, response);
        return {
          name: response?.data?.userByUId?.uFullName,
          color,
        };
      };

      await meetingManager.start();

      await configureDevices();

      setIsHost(true);
      setHostId(HostId);
      console.log("attendeeData for attendeeId", attendeeData, attendeeData!.Attendee!.AttendeeId!);
      setHostAttendeeId(attendeeData!.Attendee!.AttendeeId!);
      localStorage.setItem("hostId", hostId ?? "");
      localStorage.setItem("hostAttendeeId", attendeeData!.Attendee!.AttendeeId! ?? "");
      // Start listening for mute-all messages
      listenForMuteParticipant();

      // Start listening for live update messages
      listenForLiveUpdates();

      // Start listening for status request for new participants
      // listenForStatusRequest();

      return meetingResponse;
    },
    [meetingManager, configureDevices, listenForMuteParticipant, listenForLiveUpdates, getUser]
  );

  // Function to join a meeting (Attendee)
  const joinMeeting = useCallback(
    async (meetingId: string, participantId: string, HostId: string, originalHostId : string, hostattendeeId: string) => {
      const meetingResponse = await getChimeMeeting(meetingId);
      const attendeeResponse = await createChimeAttendee(meetingId, participantId);

      setMeetingId(meetingId);
      setAttendeeId(attendeeResponse!.Attendee!.AttendeeId!);
      localStorage.setItem('attendeeId', attendeeResponse!.Attendee!.AttendeeId!)
      setUserId(attendeeResponse!.Attendee!.ExternalUserId!);
      console.log(participantId, originalHostId, "originalhostId")

      // Ensure that media devices are allowed by the user
      meetingManager.meetingSession?.audioVideo.setDeviceLabelTrigger(() =>
        navigator.mediaDevices.getUserMedia({ audio: true, video: true })
      );

      const configuration = new MeetingSessionConfiguration(meetingResponse, attendeeResponse);

      await meetingManager.join(configuration, {
        deviceLabels: async () =>
          await navigator.mediaDevices.getUserMedia({ audio: true, video: true }),
        enableWebAudio:true
      });

      meetingManager?.meetingSession?.audioVideo?.start();

      meetingManager.getAttendee = async (chimeAttendeeId: string, externalUserId?: string) => {
        if(externalUserId?.includes("aws:MediaPipeline")) {
          console.log("Inside getUser if condition for mediapipeline", externalUserId);
          return {
            name: "Media Pipeline",
            color: "#000000",
          }
        }
        const response = await getUser({ variables: { uId: externalUserId?.split('|')[0] } });
        const color = getColorFromUUID(externalUserId?.split('|')[0]!);
        console.log("[Log] getUser response", color, response);
        return {
          name: response?.data?.userByUId?.uFullName,
          color,
        };
      };

      await meetingManager.start();

      await configureDevices();

      setIsHost(participantId === HostId);
      setHostId(HostId);
      console.log("hostAttendeeId is", hostattendeeId);
      setHostAttendeeId(hostattendeeId);
      localStorage.setItem("hostId", hostId ?? "");
      localStorage.setItem("hostAttendeeId", hostattendeeId ?? "");
      // Start listening for mute-all messages
      listenForMuteParticipant();

      // Start listening for live update messages
      listenForLiveUpdates();

      // if (participantId === hostId) {
      //   // Start listening for status request for new participants
      //   // listenForStatusRequest();
      // } else {
      //   // Request the current screen recording status from the host
      //   const message = JSON.stringify({ action: "screen-recording-status" });
      //   meetingManager.meetingSession?.audioVideo?.realtimeSendDataMessage("status-request", message);
      // }    

      return { meetingResponse, attendeeResponse };
    },
    [meetingManager, configureDevices, listenForMuteParticipant, listenForLiveUpdates, getUser]
  );

  // Toggle audio
  const toggleAudio = useCallback(async () => {
    const audioVideo = meetingManager.meetingSession?.audioVideo;
    if (!audioVideo) {
      return;
    }

    if (isAudioEnabled) {
      audioVideo.realtimeMuteLocalAudio();
      setIsAudioEnabled(false);
    } else {
      audioVideo.realtimeUnmuteLocalAudio();
      setIsAudioEnabled(true);
    }
  }, [isAudioEnabled, meetingManager]);

  // Toggle video
  const toggleVideo = useCallback(async () => {
    const audioVideo = meetingManager.meetingSession?.audioVideo;
    if (!audioVideo) {
      console.log('[Log] No audioVideo', meetingManager);
      return;
    }

    if (isVideoEnabled) {
      audioVideo.stopLocalVideoTile();
      await meetingManager.stopVideoInputDevice();
      setIsVideoEnabled(false);
    } else {
      const videoInputDevices = await audioVideo.listVideoInputDevices();
      if (videoInputDevices?.length) {
        await meetingManager.startVideoInputDevice(videoInputDevices[0].deviceId);
        audioVideo.startLocalVideoTile();
        setIsVideoEnabled(true);
      }
    }
  }, [isVideoEnabled, meetingManager, setIsVideoEnabled]);

  const startScreenShare = useCallback(
    (attendee: RosterAttendeeType & { color: string }) => {
      const audioVideo = meetingManager.meetingSession?.audioVideo;
      if (!audioVideo) {
        console.error("[log] No audioVideo available for screen sharing.");
        return;
      }
      setRemoteSharing(true);
      setRemoteSharingUser(attendee);
      const message = JSON.stringify({ action: SCREEN_SHARING, screenSharing: true, attendee });
      audioVideo.realtimeSendDataMessage(LIVE_UPDATE, message);
    },
    [meetingManager]
  );

  const stopScreenShare = useCallback(() => {
    const audioVideo = meetingManager.meetingSession?.audioVideo;
    if (!audioVideo) {
      console.error("[log] No audioVideo available for screen sharing.");
      return;
    }
    setRemoteSharing(false);
    setRemoteSharingUser(null);
    const message = JSON.stringify({
      action: SCREEN_SHARING,
      screenSharing: false,
      attendee: null,
    });
    audioVideo.realtimeSendDataMessage(LIVE_UPDATE, message);
  }, [meetingManager]);

  // Toggle screen share
  const toggleScreenShare = useCallback(
    async (attendee?: RosterAttendeeType & { color: string; }) => {
      const audioVideo = meetingManager.meetingSession?.audioVideo;
      if (!audioVideo) {
        console.error("No audioVideo available for screen sharing.");
        return;
      }

      if (isRemoteSharing) {
        setRemoteSharing(false);
        setRemoteSharingUser(null);
        const message = JSON.stringify({ action: SCREEN_SHARING, screenSharing: false, attendee: null });
        audioVideo.realtimeSendDataMessage(LIVE_UPDATE, message);
      } else if (attendee) {
        setRemoteSharing(true);
        setRemoteSharingUser(attendee);
        const message = JSON.stringify({ action: SCREEN_SHARING, screenSharing: true, attendee });
        audioVideo.realtimeSendDataMessage(LIVE_UPDATE, message);
      }

      toggleContentShare();
    },
    [isRemoteSharing, meetingManager.meetingSession?.audioVideo, toggleContentShare]
  );

  // Mute all participants
  const muteAll = useCallback(async () => {
    if (!(userId === hostId)) {
      console.warn("Only the host can mute all participants.");
      return;
    }

    const audioVideo = meetingManager?.meetingSession?.audioVideo;

    if (audioVideo) {
      try {
        const message = JSON.stringify({ action: "mute-all" });
        audioVideo.realtimeSendDataMessage(LIVE_UPDATE, message);

        // Optionally mute the host as well
        // audioVideo.realtimeMuteLocalAudio();
        // setIsAudioEnabled(false);
      } catch (error) {
        console.error("Failed to send mute-all message:", error);
        errorToast("Failed to mute all participants.");
      }
    }
  }, [meetingManager, isHost, hostId, userId]);


  const hostChange = useCallback(async () => {
    console.log("[debug] host change")
    const audioVideo = meetingManager?.meetingSession?.audioVideo;

    // if (audioVideo) {
    //   try {
    //     const message = JSON.stringify({ action: "host-changed" });
    //     audioVideo.realtimeSendDataMessage("live-update", message );

    //     // Optionally mute the host as well
    //     // audioVideo.realtimeMuteLocalAudio();
    //     // setIsAudioEnabled(false);
    //   } catch (error) {
    //     console.error("Failed to send mute-all message:", error);
    //     errorToast("Failed to mute all participants.");
    //   }
    // }
  }, [meetingManager]);

  // Mute all participants
  const muteParticipant = useCallback(async (id: string) => {
    if (!(userId === hostId)) {
      console.warn("Only the host can mute a participants.");
      return;
    }
    
    const audioVideo = meetingManager?.meetingSession?.audioVideo;

    if (audioVideo) {
      try {
        const message = JSON.stringify({ action: "mute", id: id });
        audioVideo.realtimeSendDataMessage("mute", message);

        // Optionally mute the host as well
        // audioVideo.realtimeMuteLocalAudio();
        // setIsAudioEnabled(false);
      } catch (error) {
        console.error("Failed to send mute message:", error);
        errorToast("Failed to mute a participant.");
      }
    }
  }, [meetingManager, isHost, hostId, userId]);

  const [updateWorkshopSessionRecord] = useMutation(UPDATE_WORKSHOP_MEETING_ID, {
    fetchPolicy: "no-cache",
    onCompleted: (data: any) => {
      console.log("Update workshop response:", data);
    },
    onError: (error) => {
      errorToast(error.message);
    },
  });

  // Mutation to update liveclass session record with media pipeline details
    const [updateLiveclassSessionRecord] = useMutation(UPDATE_LIVECLASS_MEDIA_PIPELINE_INFO, {
    fetchPolicy: "no-cache",
    onCompleted: (data: any) => {
      console.log("Update liveclass response:", data);
    },
    onError: (error) => {
      errorToast(error.message);
    },
  });

    // Mutation to update pta session record with media pipeline details
  //   const [updatePtaSessionRecord] = useMutation(UPDATE_PTA_MEDIA_PIPELINE_INFO, {
  //   fetchPolicy: "no-cache",
  //   onCompleted: (data: any) => {
  //     console.log("Update pta response:", data);
  //   },
  //   onError: (error) => {
  //     errorToast(error.message);
  //   },
  // });

  const [updatePtaSessionRecord] = useMutation(UPDATE_MEETING_STATUS, {
    fetchPolicy: FETCH_POLICY_NO_CACHE,
    onCompleted: (data) => {},
    onError: (err) => {}
  });

    // Functions to manage recording
    const startRecording = useCallback(async (recordingStartTime:string, currentSessionId?: string) => {
      if (startRecordingInProgress) {
        return;
      }

      setStartRecordingInProgress(true);

      console.log('inside the context startRecording======>',meetingManager.meetingId)
      if (!meetingManager.meetingId) {
        errorToast("Meeting ID is unavailable to start recording.");
        console.error("[Log] Meeting ID is unavailable to start recording.", meetingManager);
        setStartRecordingInProgress(false);
        return;
      }
  
      try {
        const response = await chimeStartRecording(meetingManager.meetingId);
        console.log("[Log] Start recording response", response);
        setMediaPipelineId(response?.MediaCapturePipeline?.MediaPipelineId || null);
        console.log(response?.MediaCapturePipeline?.MediaPipelineId, "media", response?.MediaCapturePipeline?.MediaPipelineArn)
        dispatch(setCurrentSessionDetail({  mediaPipelineArn : response?.MediaCapturePipeline?.MediaPipelineArn ?? null , mediaPipelineId : response?.MediaCapturePipeline?.MediaPipelineId ?? null }));
        console.log("inside the start recording function", hostId, hostAttendeeId);
        const localStorageHostId = localStorage.getItem("hostId");
        const localStorageHostAttendeeId = localStorage.getItem("hostAttendeeId");
        console.log("localStorageHostId", localStorageHostId, localStorageHostAttendeeId);
         const messageForRecording = JSON.stringify({
           action: "host-changed",
           hostId: hostId || localStorageHostId ,
           hostAttendeeId: hostAttendeeId || localStorageHostAttendeeId,
           mediaPipelineId: response?.MediaCapturePipeline?.MediaPipelineId,
           mediaPipelineArn: response?.MediaCapturePipeline?.MediaPipelineArn,
         });

         meetingManager.meetingSession?.audioVideo?.realtimeSendDataMessage(
           "live-update",
           messageForRecording
         );

        if (currentSessionId) {
          if(meetComponent === 'liveclass') {
            updateLiveclassSessionRecord({
              variables: {
                cId: selectedClassRoomId,
                sjId: selectedSubjectId,
                sessionId: currentSessionId,
                mediaPipelineArn: response?.MediaCapturePipeline?.MediaPipelineArn,
                mediaPipelineId: response?.MediaCapturePipeline?.MediaPipelineId || null,
                sourceDestinationArn: `${response?.sinkArnDestination}/${meetingManager.meetingId}`
              }
            });
          } else if (meetComponent === 'workshop') {
              updateWorkshopSessionRecord({
                variables: {
                  updateInput: {
                    mediaPipelineArn: response?.MediaCapturePipeline?.MediaPipelineArn,
                    sourceDestinationArn:`${response?.sinkArnDestination}/${meetingManager.meetingId}`,
                    mediaPipelineId : response?.MediaCapturePipeline?.MediaPipelineId || null
                  },
                  wsId: currentSessionId,
                },
              });
          } else if (meetComponent === 'pta') {
            console.log("Updating PTA session record with media pipeline info", response?.MediaCapturePipeline?.MediaPipelineArn, response?.MediaCapturePipeline?.MediaPipelineId, currentSessionId );
              updatePtaSessionRecord({
                variables: {
                  updateInput: {
                    mediaPipelineArn: response?.MediaCapturePipeline?.MediaPipelineArn,
                    mediaPipelineId: response?.MediaCapturePipeline?.MediaPipelineId || null,
                    sourceDestinationArn: `${response?.sinkArnDestination}/${meetingManager.meetingId}`
                  },
                  mId: currentSessionId
                }
              })
          }
        }


        setMediaPipelineArn(response?.MediaCapturePipeline?.MediaPipelineArn || null);
        setIsRecording(true);
        setRecordingStartTime(recordingStartTime);
        const message = JSON.stringify({ action: SCREEN_RECORDING, time: recordingStartTime });
        meetingManager.meetingSession?.audioVideo?.realtimeSendDataMessage(LIVE_UPDATE, message);
      } catch (error) {
        errorToast("Failed to start the recording.");
        console.error("[Log] Failed to start the recording.", error);
      } finally {
        setStartRecordingInProgress(false);
      }
    }, [meetingManager, startRecordingInProgress, meetComponent]);

    const handRaise = useCallback(() => {
      meetingManager.meetingSession?.audioVideo?.realtimeSendDataMessage(
          'hand-raise',
          JSON.stringify({
              attendeeId: meetingManager?.meetingSessionConfiguration?.credentials?.attendeeId,
              isHandRaised: true,
          }),
      );
    }, []);

    const handDown = useCallback(() => {
      meetingManager.meetingSession?.audioVideo?.realtimeSendDataMessage(
          HAND_RAISE,
          JSON.stringify({
              attendeeId: meetingManager?.meetingSessionConfiguration?.credentials?.attendeeId,
              isHandRaised: false,
          }),
      );
    }, []);

    const toggleHandRaise = useCallback(() => {
      if(isHandRaised) {
          handDown();
      } else{
          handRaise();
      }
      setIsHandRaised(!isHandRaised);
    }, [isHandRaised]);

    const subscribeToHandRaise = () => {
      const meetingSession = meetingManager?.meetingSession;
      meetingSession?.audioVideo?.realtimeSubscribeToReceiveDataMessage(
        HAND_RAISE, (data) => {
          const handRaiseData: any = JSON.parse(data.text());
          console.log("hand raiseed : ", data)
            const attendeeId: any = handRaiseData.attendeeId;
            try {
              participantsRef.current[attendeeId].handRaised = handRaiseData.isHandRaised;
               const prevAttendeesData = attendeesDataRef.current.map((attendee: any) => {
                  if(attendee.chimeAttendeeId === attendeeId){
                      return {...attendee, handRaised : handRaiseData.isHandRaised}
                  } else{
                      return attendee
                  }
              })
              setAttendeesData(prevAttendeesData);
              setParticipantsList(prevAttendeesData);
              attendeesDataRef.current = prevAttendeesData;
            } catch( err: any) {
              console.log("hand raised error: ", err);
            }
        }
      )
    };
  
    const stopRecording = useCallback(async () => {
      if (stopRecordingInProgress) {
        return;
      }

      setStopRecordingInProgress(true);

      console.log('inside the stop recording function');
      if (!mediaPipelineId || !mediaPipelineArn || !meetingManager.meetingId) {
        console.error("[Log] Unable to stop the recording.", { mediaPipelineId, mediaPipelineArn, meetingManager });
        setStopRecordingInProgress(false);
        return;
      }
  
      try {
        await chaimeStopRecording(mediaPipelineId, meetingManager.meetingId, mediaPipelineArn);
        setIsRecording(false);
        setMediaPipelineId(null);
        setMediaPipelineArn(null);
        setRecordingStartTime(null);
        dispatch(setCurrentSessionDetail({  mediaPipelineArn : null , mediaPipelineId : null }));
        console.log("inside the stop recording function", hostId, hostAttendeeId);
        const localStorageHostId = localStorage.getItem("hostId");
        const localStorageHostAttendeeId = localStorage.getItem("hostAttendeeId");
        console.log("localStorageHostId", localStorageHostId, localStorageHostAttendeeId);
        const messageForRecording = JSON.stringify({
           action: "host-changed",
           hostId: hostId || localStorageHostId,
           hostAttendeeId: hostAttendeeId || localStorageHostAttendeeId,
           mediaPipelineId: null,
           mediaPipelineArn: null,
         });

         meetingManager.meetingSession?.audioVideo?.realtimeSendDataMessage(
           "live-update",
           messageForRecording
         );
        const message = JSON.stringify({ action: SCREEN_RECORDING, time: null });
        meetingManager.meetingSession?.audioVideo?.realtimeSendDataMessage(LIVE_UPDATE, message);
      } catch (error) {
        errorToast("Failed to stop the recording.");
        console.error("[Log] Failed to stop the recording.", error);
      } finally {
        setStopRecordingInProgress(false);
        setIsRecording(false);
        setRecordingStartTime(null)
         setMediaPipelineId(null);
        setMediaPipelineArn(null);
      }
    }, [stopRecordingInProgress, mediaPipelineId, mediaPipelineArn, meetingManager, userId, hostId]);

  // Leave meeting
  const leaveMeeting = useCallback(async () => {
    console.log("leaveMeeting:", { isHost, isRecording });

    if (attendeeId === remoteSharingUser?.chimeAttendeeId) {
      stopScreenShare();
    }
    if(meetingId && userId) {
      trackMettingEvent("l", {
        meetingId: meetingId!,
        participantId: userId!?.split('|')[0],
      });
    }

    if(meetingId && userId){
      updateUserProperties({
          meetingId: meetingId!, // overwrite old value
      });
      captureEvent(userId || '', {
        role: role,
        loginType: loginpage,
        meetingId: meetingId!
      });
    }
    

    // if ((userId === hostId) && isRecording) {
    //   await stopRecording();
    // }

    const audioVideo = meetingManager.meetingSession?.audioVideo;
    if (audioVideo) {
      audioVideo.realtimeUnsubscribeFromReceiveDataMessage(LIVE_UPDATE);
      // Stop the local meeting session
      audioVideo.stop();
    }

    if (meetingId && attendeeId) {
      await meetingManager.leave();
      await deleteChimeAttendee(meetingId, attendeeId);
    }

    setAttendeeId(null);
    setIsAudioEnabled(true);
    setIsVideoEnabled(false);
    setUserId(null);
    setIsHost(false);
    setIsRecording(false);
    setRecordingStartTime(null);
    setStartRecordingInProgress(false);
    setStopRecordingInProgress(false);
  }, [
    isHost,
    isRecording,
    meetingManager,
    attendeeId,
    remoteSharingUser?.chimeAttendeeId,
    meetingId,
    setIsVideoEnabled,
    stopScreenShare,
    stopRecording,
  ]);

  // End meeting (Host only)
  const endMeeting = useCallback(async () => {
    // if (!isHost) {
    //   console.warn("Only the host can end the meeting.");
    //   return;
    // }
    // if (attendeeId !== hostId) {
    //   console.warn("Only the host can end the meeting.");
    //   return;
    // }
    if (attendeeId === remoteSharingUser?.chimeAttendeeId) {
      stopScreenShare();
    }
    setRemoteSharing(false);
    setRemoteSharingUser(null);
    const meetingSession = meetingManager.meetingSession;
    if (!meetingSession) {
      console.error("No active meeting session to end.");
      return;
    }

    const meetingId = meetingSession.configuration.meetingId;
    if (!meetingId) {
      console.error("Meeting ID is missing.");
      return;
    }
    
    if(isRecording){
      await stopRecording();
    }

    if(meetingId) {
      trackMettingEvent("e", { meetingId: meetingId });
    }

    if(meetingId && userId){
      updateUserProperties({
          meetingId: meetingId!, // overwrite old value
      });
      captureEvent(userId || '', {
        role: role,
        loginType: loginpage,
        meetingId: meetingId!
      });
    }

    const audioVideo = meetingManager.meetingSession?.audioVideo;
    if (audioVideo) {
      audioVideo.realtimeUnsubscribeFromReceiveDataMessage(LIVE_UPDATE);
      // Stop the local meeting session
      audioVideo.stop();
    }

    // Leave the meeting
    await meetingManager.leave();
    await deleteChimeMeeting(meetingId);


    // Reset state
    setAttendeeId(null);
    setUserId(null);
    setIsAudioEnabled(true);
    setIsVideoEnabled(false);
    setIsHost(false);
    setIsRecording(false);
    setRecordingStartTime(null);
    setStartRecordingInProgress(false);
    setStopRecordingInProgress(false);

    console.log("[Log] Meeting ended successfully.");
  }, [attendeeId, remoteSharingUser?.chimeAttendeeId, meetingManager, isRecording, setIsVideoEnabled, stopScreenShare, stopRecording]);

  const value = useMemo(
    () => ({
      createMeeting,
      joinMeeting,
      toggleAudio,
      toggleVideo,
      leaveMeeting,
      startRecording,
      stopRecording,
      endMeeting,
      attendeeId,
      isHost,
      isAudioEnabled,
      isVideoEnabled,
      isRecording,
      participantsRef,
      attendeesDataRef,
      attendeesData,
      setAttendeesData,
      participantList,
      setParticipantsList,
      toggleHandRaise,
      subscribeToHandRaise,
      isHandRaised,
      setIsHandRaised,
      hostId,
      setHostId,
      setHostAttendeeId,
      hostAttendeeId,
      meetingId,
      muteAll,
      setRemoteSharing,
      isRemoteSharing,
      setRemoteSharingUser,
      remoteSharingUser,
      recordingStartTime,
      muteParticipant,
      toggleScreenShare,
      startScreenShare,
      stopScreenShare,
      setReadCount,
      readCount,
      setRecordingStartTime,
      userId,
      setIsRecording,
      startRecordingInProgress,
      stopRecordingInProgress,
      isShowParticipants,
      setIsShowParticipants,
      hasNewChatMessages,
      setHasNewChatMessages,
      showChat,
      setShowChat,
      mediaPipelineId,
      mediaPipelineArn,
      setMediaPipelineArn,
      setMediaPipelineId
    }),
    [
      createMeeting,
      joinMeeting,
      toggleAudio,
      toggleVideo,
      leaveMeeting,
      startRecording,
      stopRecording,
      endMeeting,
      attendeeId,
      isHost,
      isAudioEnabled,
      isVideoEnabled,
      isRecording,
      participantsRef,
      attendeesDataRef,
      attendeesData,
      setAttendeesData,
      participantList,
      setParticipantsList,
      toggleHandRaise,
      subscribeToHandRaise,
      isHandRaised,
      setIsHandRaised,
      hostId,
      setHostId,
      setHostAttendeeId,
      hostAttendeeId,
      meetingId,
      muteAll,
      setRemoteSharing,
      isRemoteSharing,
      setRemoteSharingUser,
      remoteSharingUser,
      recordingStartTime,
      muteParticipant,
      toggleScreenShare,
      startScreenShare,
      stopScreenShare,
      setReadCount,
      readCount,
      setRecordingStartTime,
      userId,
      setIsRecording,
      startRecordingInProgress,
      stopRecordingInProgress,
      isShowParticipants,
      setIsShowParticipants,
      hasNewChatMessages,
      setHasNewChatMessages,
      showChat,
      setShowChat,
      mediaPipelineId,
      mediaPipelineArn,
      setMediaPipelineArn,
      setMediaPipelineId
    ]
  );

  return (
    <ChimeMeetingContext.Provider 
      value={value}>
      {children}
    </ChimeMeetingContext.Provider>
  );
};

// Custom hook for consuming the context
export const useChimeMeeting = (): MeetingContextType => {
  const context = useContext(ChimeMeetingContext);
  if (!context) {
    errorToast("useChimeMeeting must be used within a ChimeMeetingProvider");
    throw new Error("useChimeMeeting must be used within a ChimeMeetingProvider");
  }
  return context;
};
